import { useEffect, useState, useCallback } from 'react'
import { authedFetch } from '../api'
import { useParams, useNavigate } from 'react-router-dom'
import { useAppDispatch } from '../hooks'
import { deleteAgent } from '../slices/agentsSlice'
import SidebarNavigation from '../components/SidebarNavigation'
import AgentDetailHeader from '../components/AgentDetailHeader'
import AgentDetailMeta from '../components/AgentDetailMeta'
import AgentDetailFields from '../components/AgentDetailFields'
import AgentDetailResults from '../components/AgentDetailResults'
import './AgentDetailPage.css'

export interface AgentTaskResult {
  status: 'pass' | 'fail' | 'dsq' | 'disagree'
  cost: number
  time_seconds: number
  failure_id: string
  failure_short_id: string
  failure_title: string
  run_id: string
  run_name: string
}

interface AgentDetail {
  id: string
  name: string
  tool_command: string
  prompt: string
  clone_branch: string
  enabled: boolean
  accuracy: number | null
  avg_cost: number | null
  total_score: number
  failure_count: number
  pass_count: number
  fail_count: number
  dsq_count: number
  disagree_count: number
  created_at: string
  task_results: AgentTaskResult[]
}

function AgentDetailPage() {
  const { agentId } = useParams<{ agentId: string }>()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [agent, setAgent] = useState<AgentDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editForm, setEditForm] = useState({ name: '', tool_command: '', prompt: '', clone_branch: '' })

  useEffect(() => {
    if (!agentId) return
    setLoading(true)
    fetch(`/api/agents/${agentId}`)
      .then((res) => {
        if (res.status === 404) {
          setLoading(false)
          return null
        }
        if (!res.ok) throw new Error('Failed to fetch agent')
        return res.json() as Promise<AgentDetail>
      })
      .then((data) => {
        if (data) {
          setAgent(data)
          setEditForm({
            name: data.name,
            tool_command: data.tool_command,
            prompt: data.prompt,
            clone_branch: data.clone_branch,
          })
        }
        setLoading(false)
      })
      .catch((err) => {
        setError(err.message)
        setLoading(false)
      })
  }, [agentId])

  const handleDelete = useCallback(async () => {
    if (!agentId) return
    setDeleteError(null)
    try {
      const result = await dispatch(deleteAgent(agentId)).unwrap()
      if (result) {
        navigate('/agents')
      }
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete agent')
    }
  }, [dispatch, agentId, navigate])

  const handleSave = useCallback(async () => {
    if (!agentId) return
    setSaving(true)
    try {
      const res = await authedFetch(`/api/agents/${agentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      })
      if (!res.ok) throw new Error('Failed to update agent')
      const updated = await res.json()
      setAgent((prev) => prev ? { ...prev, ...updated } : prev)
      setEditing(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }, [agentId, editForm])

  const handleCancel = useCallback(() => {
    if (agent) {
      setEditForm({
        name: agent.name,
        tool_command: agent.tool_command,
        prompt: agent.prompt,
        clone_branch: agent.clone_branch,
      })
    }
    setEditing(false)
  }, [agent])

  const handleToggleEnabled = useCallback(async () => {
    if (!agentId || !agent) return
    const newEnabled = !agent.enabled
    try {
      const res = await authedFetch(`/api/agents/${agentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...agent, enabled: newEnabled }),
      })
      if (!res.ok) throw new Error('Failed to update agent')
      setAgent((prev) => prev ? { ...prev, enabled: newEnabled } : prev)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle enabled')
    }
  }, [agentId, agent])

  const handleEditFormChange = useCallback((updates: Partial<{ tool_command: string; clone_branch: string; prompt: string }>) => {
    setEditForm((f) => ({ ...f, ...updates }))
  }, [])

  return (
    <div data-testid="agent-detail-page" className="agent-detail-page">
      <SidebarNavigation />

      <main className="agent-detail-main app-page-main">
        {loading && (
          <div data-testid="agent-detail-loading" className="agent-detail-loading">
            Loading agent details...
          </div>
        )}

        {error && (
          <div data-testid="agent-detail-error" className="agent-detail-error">
            {error}
          </div>
        )}

        {!loading && !error && !agent && (
          <div data-testid="agent-detail-not-found" className="agent-detail-not-found">
            Agent not found.
          </div>
        )}

        {!loading && agent && (
          <>
            <header className="app-page-sticky-header">
              <div className="app-page-sticky-header-inner">
                <AgentDetailHeader
                  agentName={agent.name}
                  editing={editing}
                  saving={saving}
                  showDeleteConfirm={showDeleteConfirm}
                  editName={editForm.name}
                  onEditNameChange={(value) => setEditForm((f) => ({ ...f, name: value }))}
                  onEdit={() => setEditing(true)}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  onDelete={() => setShowDeleteConfirm(true)}
                  onDeleteConfirm={handleDelete}
                  onDeleteCancel={() => setShowDeleteConfirm(false)}
                />
              </div>
            </header>

            {deleteError && (
              <div data-testid="agent-detail-delete-error" className="agent-detail-error">
                {deleteError}
              </div>
            )}

            <AgentDetailMeta
              createdAt={agent.created_at}
              accuracy={agent.accuracy}
              avgCost={agent.avg_cost}
            />

            <div className="agent-detail-enabled-toggle">
              <label>
                <input
                  type="checkbox"
                  checked={agent.enabled}
                  onChange={handleToggleEnabled}
                />
                <span>{agent.enabled ? 'Enabled for failure runs' : 'Disabled for failure runs'}</span>
              </label>
            </div>

            <AgentDetailFields
              toolCommand={agent.tool_command}
              cloneBranch={agent.clone_branch}
              prompt={agent.prompt}
              editing={editing}
              editForm={editForm}
              onEditFormChange={handleEditFormChange}
            />

            <AgentDetailResults
              totalScore={agent.total_score}
              failureCount={agent.failure_count}
              passTasks={agent.task_results.filter(t => t.status === 'pass')}
              failTasks={agent.task_results.filter(t => t.status === 'fail')}
              dsqTasks={agent.task_results.filter(t => t.status === 'dsq')}
              disagreeTasks={agent.task_results.filter(t => t.status === 'disagree')}
            />
          </>
        )}
      </main>
    </div>
  )
}

export default AgentDetailPage
